import { useState } from 'react';
import { CalculationHistory, ApiResponse } from './types';
import { MaterialSelector } from './components/MaterialSelector';
import { VeneerInput } from './components/VeneerInput';
import { PieceInput } from './components/PieceInput';
import { KerfInput } from './components/KerfInput';
import { CalculateButton } from './components/CalculateButton';
import { ResultDisplay } from './components/ResultDisplay';
import { CuttingDiagram } from './components/CuttingDiagram';
import { ExportButtons } from './components/ExportButtons';
import { HistoryPanel } from './components/HistoryPanel';
import { ErrorMessage } from './components/ErrorMessage';
import { LoadingSpinner } from './components/LoadingSpinner';
import { useCalculationForm } from './hooks/useCalculationForm';
import { useCalculationHistory } from './hooks/useCalculationHistory';

function App() {
  const [result, setResult] = useState<ApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    formData,
    errors,
    touched,
    isValid,
    handleFieldChange,
    handleSubmit,
    resetForm,
  } = useCalculationForm();

  const {
    history,
    addToHistory,
    clearHistory,
    removeFromHistory,
  } = useCalculationHistory();

  const handleCalculate = async (data: typeof formData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await handleSubmit(data);
      setResult(response);
      addToHistory(response.inputData, response.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '計算中にエラーが発生しました');
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHistorySelect = (historyItem: CalculationHistory) => {
    // 履歴から選択されたアイテムでフォームを更新
    const { input } = historyItem;
    handleFieldChange('materialName', input.materialName);
    handleFieldChange('veneerWidth1', input.veneerWidth1.toString());
    handleFieldChange('veneerHeight1', input.veneerHeight1.toString());
    handleFieldChange('veneerWidth2', input.veneerWidth2.toString());
    handleFieldChange('veneerHeight2', input.veneerHeight2.toString());
    handleFieldChange('pieceWidth', input.pieceWidth.toString());
    handleFieldChange('pieceHeight', input.pieceHeight.toString());
    handleFieldChange('kerf', input.kerf.toString());
    handleFieldChange('allowRotation1', input.allowRotation1);
    handleFieldChange('allowRotation2', input.allowRotation2);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-300 shadow-sm">
        <div className="container py-6">
          <h1 className="text-3xl font-bold text-gray-900 text-center">
            歩留まり計算ツール
          </h1>
          <p className="text-gray-600 text-center mt-2 leading-relaxed">
            大きな板材から小さな板を最適に切り出すための歩留まり計算ツールです
          </p>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 入力フォーム */}
          <div className="lg:col-span-2 space-y-6">
            {/* エラーメッセージ */}
            {error && (
              <ErrorMessage message={error} onClose={() => setError(null)} />
            )}

            {/* 入力カード */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                計算条件を入力
              </h2>
              
              
              <form 
                className="space-y-6" 
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isValid) {
                    handleCalculate(formData);
                  }
                }}
              >
                <MaterialSelector
                  value={formData.materialName}
                  onChange={(value) => handleFieldChange('materialName', value)}
                  error={errors.materialName}
                  touched={touched.materialName}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <VeneerInput
                    title="板材1"
                    width={formData.veneerWidth1}
                    height={formData.veneerHeight1}
                    allowRotation={formData.allowRotation1}
                    onWidthChange={(value) => handleFieldChange('veneerWidth1', value)}
                    onHeightChange={(value) => handleFieldChange('veneerHeight1', value)}
                    onRotationChange={(value) => handleFieldChange('allowRotation1', value)}
                    widthError={errors.veneerWidth1}
                    heightError={errors.veneerHeight1}
                    widthTouched={touched.veneerWidth1}
                    heightTouched={touched.veneerHeight1}
                  />

                  <VeneerInput
                    title="板材2"
                    width={formData.veneerWidth2}
                    height={formData.veneerHeight2}
                    allowRotation={formData.allowRotation2}
                    onWidthChange={(value) => handleFieldChange('veneerWidth2', value)}
                    onHeightChange={(value) => handleFieldChange('veneerHeight2', value)}
                    onRotationChange={(value) => handleFieldChange('allowRotation2', value)}
                    widthError={errors.veneerWidth2}
                    heightError={errors.veneerHeight2}
                    widthTouched={touched.veneerWidth2}
                    heightTouched={touched.veneerHeight2}
                  />
                </div>

                <PieceInput
                  width={formData.pieceWidth}
                  height={formData.pieceHeight}
                  onWidthChange={(value) => handleFieldChange('pieceWidth', value)}
                  onHeightChange={(value) => handleFieldChange('pieceHeight', value)}
                  widthError={errors.pieceWidth}
                  heightError={errors.pieceHeight}
                  widthTouched={touched.pieceWidth}
                  heightTouched={touched.pieceHeight}
                />

                <KerfInput
                  value={formData.kerf}
                  onChange={(value) => handleFieldChange('kerf', value)}
                  error={errors.kerf}
                  touched={touched.kerf}
                />

                <div className="flex flex-col sm:flex-row gap-4">
                  <CalculateButton
                    isLoading={isLoading}
                    isValid={isValid}
                    className="flex-1"
                  />
                  
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary flex-1"
                    disabled={isLoading}
                  >
                    リセット
                  </button>
                </div>
              </form>
            </div>

            {/* 結果表示 */}
            {isLoading && (
              <div className="card">
                <LoadingSpinner message="計算中..." />
              </div>
            )}

            {result && !isLoading && (
              <>
                <ResultDisplay result={result.result} />
                <CuttingDiagram svg={result.svg} />
                <ExportButtons 
                  result={result.result}
                  svg={result.svg}
                  inputData={result.inputData}
                  history={history}
                />
              </>
            )}
          </div>

          {/* サイドバー - 履歴 */}
          <div className="lg:col-span-1">
            <HistoryPanel
              history={history}
              onSelect={handleHistorySelect}
              onClear={clearHistory}
              onRemove={removeFromHistory}
            />
          </div>
        </div>
      </main>

      {/* フッター */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="container py-8">
          <div className="text-center text-gray-600 text-sm">
            <p>&copy; 2025 歩留まり計算ツール. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;